import { Fragment, MouseEvent, ReactNode } from 'react';
import { Box, Button } from '@mui/material';
import { Plus } from '@ankr.com/ui';
import { Chain } from '@ankr.com/chains-list';

import { ButtonMetamask } from 'uiKit/ButtonMetamask';
import { ChainProtocolContext } from 'domains/chains/screens/ChainPage/constants/ChainProtocolContext';
import { ChainSelectorContent } from 'modules/common/components/ChainSelectorContent';
import { isMultichain } from 'modules/chains/utils/isMultichain';
import { Dialog } from 'uiKit/Dialog';
import { useTranslation } from 'modules/i18n/hooks/useTranslation';
import { GuardResolution } from 'modules/common/components/GuardResolution';
import { useAccount } from 'modules/account/hooks/useAccount';
import { useAccountStatus } from 'modules/accountStatus/hooks/useAccountStatus';
import { useCheckAccountPermission } from 'modules/account/hooks/useCheckAccountPermission';
import { AccountPermission } from 'modules/permissions/constants';

import { useNetworksButtonTranslations } from './translation';
import { useAddNetworkButtonStyles } from './useAddNetworkButtonStyles';
import { useAddNetworkButton } from './hooks/useAddNetworkButton';

interface IAddNetworkProps {
  chain: Chain;
  className?: string;
  label?: ReactNode;
  size?: 'large' | 'medium' | 'small';
  hasChainSelector?: boolean;
  hasPlaceholder?: boolean;
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
}

const onDialogClick = (event: MouseEvent<HTMLElement | HTMLButtonElement>) =>
  event.stopPropagation();

export const AddNetworkButton = ({
  chain,
  className,
  hasChainSelector,
  hasPlaceholder,
  label,
  onClick,
  size,
}: IAddNetworkProps) => {
  const { isFreemium } = useAccountStatus();

  const hasBillingRoleAccess = useCheckAccountPermission({
    permission: AccountPermission.Billing,
  });

  const hasToUpgradeButNoAccess =
    chain.premiumOnly && isFreemium && !hasBillingRoleAccess;

  const { isAccountInitializing } = useAccount();

  const {
    chainProtocolContext,
    chainSubTypeTab,
    chainSubTypeTabs,
    chainTypeTab,
    chainTypeTabs,
    groupID,
    groupTab,
    groupTabs,
    groups,
    handleButtonClick,
    isOpened,
    onButtonClick,
    onClose,
    selectGroup,
    shouldHideButton,
  } = useAddNetworkButton({
    chain,
    hasChainSelector,
    onClick,
    onDialogClick,
  });

  const { keys, t } = useTranslation(useNetworksButtonTranslations);

  const { classes } = useAddNetworkButtonStyles();

  if (shouldHideButton || hasToUpgradeButNoAccess) {
    if (hasPlaceholder) {
      return <Box className={classes.placeholder} />;
    }

    return null;
  }

  const WrapperComponent = hasChainSelector
    ? ChainProtocolContext.Provider
    : Fragment;

  return (
    <GuardResolution protectedResolution="mdDown">
      <WrapperComponent value={chainProtocolContext}>
        <ButtonMetamask
          className={className}
          isDisabled={isAccountInitializing}
          label={label}
          onClick={onButtonClick}
          size={size}
        />

        {hasChainSelector && (
          <Dialog
            onClick={onDialogClick}
            onClose={onClose}
            open={isOpened}
            paperClassName={classes.addNetworkDialog}
            shouldStopPropagationOnClose
            title={t(keys.selectNetwork)}
          >
            <ChainSelectorContent
              className={classes.addNetworkChainSelector}
              chainSubTypeTab={chainSubTypeTab}
              chainSubTypeTabs={chainSubTypeTabs}
              chainTypeTab={chainTypeTab}
              chainTypeTabs={chainTypeTabs}
              groupID={groupID}
              groupTab={groupTab}
              groupTabs={groupTabs}
              groups={groups}
              hasGroupSelector={false}
              selectGroup={selectGroup}
              isProtocolSwitcherHidden
              isGroupSelectorAutoWidth={false}
              isSubchainSelectorHidden={isMultichain(chain.id)}
            />

            <Button
              disabled={!handleButtonClick}
              onClick={handleButtonClick}
              startIcon={<Plus />}
            >
              {t(keys.addNetwork)}
            </Button>
          </Dialog>
        )}
      </WrapperComponent>
    </GuardResolution>
  );
};
